import { BrandWordmark } from "@/components/brand-wordmark";
import { MessageSources } from "@/components/chat/message-sources";
import { Markdown } from "@/components/ui/markdown";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { ReadSharedChatDto } from "@package/types";
import { createRoute, useParams } from "@tanstack/react-router";
import { Bot, Loader2, User } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { rootRoute } from "./__root";

export const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/share/$token",
  component: SharedChatPage
});

function SharedChatPage() {
  const { t } = useTranslation();
  const { token } = useParams({ from: "/share/$token" });
  const [chat, setChat] = useState<ReadSharedChatDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    api
      .get<ReadSharedChatDto>(`/chats/shared/${token}`)
      .then((data) => {
        if (!cancelled) setChat(data);
      })
      .catch((error) => {
        console.error("Failed to load shared chat:", error);
        if (!cancelled) setChat(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl px-4 py-8 space-y-6">
        <BrandWordmark className="h-10" />

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            {t("common.loading")}
          </div>
        ) : !chat ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            {t("share.notFound")}
          </p>
        ) : (
          <>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold">
                {chat.title || t("chats.noTitle")}
              </h1>
              <p className="text-sm text-muted-foreground">
                {`${new Date(chat.createdAt).toLocaleDateString()} · ${t("share.readOnly")}`}
              </p>
            </div>

            {chat.messages.length === 0 ? (
              <p className="py-12 text-center text-sm text-muted-foreground">
                {t("chat.noMessages")}
              </p>
            ) : (
              // ponytail: slim copy of the bubble in ChatDetailPage without the
              // role-gated badges, feedback and export controls
              <div className="space-y-4">
                {chat.messages.map((message) => (
                  <div
                    key={message.id}
                    className={cn(
                      "flex gap-3",
                      message.type === "USER" ? "justify-end" : "justify-start"
                    )}
                  >
                    {message.type !== "USER" && (
                      <div className="flex-shrink-0 size-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Bot className="size-4 text-primary-text" />
                      </div>
                    )}
                    <div
                      className={cn(
                        "rounded-lg px-4 py-2 max-w-[80%]",
                        message.type === "USER"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      )}
                    >
                      {message.type === "USER" ? (
                        <p className="text-sm whitespace-pre-wrap">
                          {message.content}
                        </p>
                      ) : (
                        <Markdown content={message.content} />
                      )}
                      {message.type === "AGENT" &&
                        message.sources.length > 0 && (
                          <MessageSources sources={message.sources} />
                        )}
                      <div
                        className={cn(
                          "text-xs mt-1.5",
                          message.type === "USER"
                            ? "text-primary-foreground/70"
                            : "text-muted-foreground"
                        )}
                      >
                        {new Date(message.createdAt).toLocaleTimeString()}
                      </div>
                    </div>
                    {message.type === "USER" && (
                      <div className="flex-shrink-0 size-8 rounded-full bg-primary flex items-center justify-center">
                        <User className="size-4 text-primary-foreground" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
